import type { Location, NavigateFunction } from 'react-router-dom'
import * as R from 'ramda'
import type { Dispatch } from 'redux'

import { ConversationTransport } from '~/api/graphql/generated/types'
import {
  ConversationStatus,
  MessageStatus,
  MessageType,
} from '~/constants/communications'
import { EasterEggEvents } from '~/types'
import type {
  ConversationFile,
  ConversationMessage,
} from '~/types/entities/conversationMessages'
import type { Conversation } from '~/types/entities/conversations'

import {
  messageFormattingAreaFocused,
  sendConversationDraftMessage,
  sendConversationMessage,
  updateMessage,
} from '../actions/conversationMessages'
import { toggleArchiveForConversation } from '../actions/conversations'
import { queueEasterEggEvent } from '../actions/easterEgg'

interface ConversationFormattingStateParams {
  canSendSmsToClient: boolean
  hasLatestMessage: boolean
  isLatestMessageInbound: boolean
  isLogicalSMSThreadingEnabled: boolean
  isMarkMessagesAsMedicalEnabled: boolean
  isUnifiedConversationTransportationThreadingEnabled: boolean
  messageFormattingAreaExpanded: boolean
  status: string | null | undefined
  totalCount: number
  transport: string | null | undefined
}

export const getConversationFormattingState = ({
  status,
  transport,
  isUnifiedConversationTransportationThreadingEnabled,
  isLogicalSMSThreadingEnabled,
  isMarkMessagesAsMedicalEnabled,
  messageFormattingAreaExpanded,
  canSendSmsToClient,
  isLatestMessageInbound,
  totalCount,
  hasLatestMessage,
}: ConversationFormattingStateParams) => {
  const isDeliveryFailed = status === ConversationStatus.DELIVERY_FAILED
  const isArchived = status === ConversationStatus.ARCHIVED
  const showOnlyOpenThreadButton =
    isUnifiedConversationTransportationThreadingEnabled && isArchived
  const isSmsTransport = transport === ConversationTransport.Sms
  const internalNoteOnly = transport === ConversationTransport.LogPhoneCall
  const canSendMessage = isSmsTransport ? canSendSmsToClient : !internalNoteOnly
  const showSendButton =
    messageFormattingAreaExpanded || !isMarkMessagesAsMedicalEnabled
  const showReplyButton =
    (isMarkMessagesAsMedicalEnabled || isLogicalSMSThreadingEnabled) &&
    !messageFormattingAreaExpanded &&
    !internalNoteOnly &&
    !showOnlyOpenThreadButton
  const showReplyInNewThreadButton =
    isLogicalSMSThreadingEnabled &&
    totalCount > 1 &&
    hasLatestMessage &&
    isLatestMessageInbound &&
    !showOnlyOpenThreadButton

  return {
    isDeliveryFailed,
    internalNoteOnly,
    canSendMessage,
    showSendButton,
    showReplyButton,
    showReplyInNewThreadButton,
  }
}

interface ArchiveActionTitleParams {
  isLogicalSMSThreadingEnabled: boolean
  status: string | null | undefined
  t: (key: string) => string
}

export const getArchiveActionTitle = ({
  status,
  isLogicalSMSThreadingEnabled,
  t,
}: ArchiveActionTitleParams): string => {
  const archivedTitle = isLogicalSMSThreadingEnabled
    ? t('Communications:MESSAGE_FORMATTING_AREA.ARCHIVE_THREAD')
    : t('Communications:MESSAGE_FORMATTING_AREA.ARCHIVE_CONVERSATION')

  const openTitle = isLogicalSMSThreadingEnabled
    ? t('Communications:MESSAGE_FORMATTING_AREA.OPEN_THREAD')
    : t('Common:OPEN_CONVERSATION')

  return status === ConversationStatus.ARCHIVED ? openTitle : archivedTitle
}

export const syncIsMedicalValue = (
  editMessageIsMedical: boolean | undefined,
  defaultCheckboxValue: boolean,
  setIsMedicalValue: (value: boolean) => void,
) => {
  if (!R.isNil(editMessageIsMedical)) {
    setIsMedicalValue(editMessageIsMedical)
  } else {
    setIsMedicalValue(defaultCheckboxValue)
  }
}

export const handleInitialLocationState = (
  location: Location,
  dispatch: Dispatch,
  navigate: NavigateFunction,
  closeFormattingArea: () => void,
) => {
  const openFromNewThreadReply = (
    location.state as { openMessageFormattingArea?: boolean } | null
  )?.openMessageFormattingArea

  if (openFromNewThreadReply) {
    dispatch(messageFormattingAreaFocused())
    navigate(location.pathname, { replace: true, state: {} })
  } else {
    closeFormattingArea()
  }
}

export const getSendActionName = (
  selectedTransport: string,
  t: (key: string) => string,
): string => {
  if (selectedTransport === MessageType.INTERNAL_NOTE) {
    return t('Common:SAVE_ACTION')
  }

  if (selectedTransport === ConversationTransport.LogPhoneCall) {
    return t('Common:LOG_PHONE_CALL')
  }

  return t('Common:SEND_ACTION')
}

interface DispatchMessageParams {
  conversationId: string
  editMessage: ConversationMessage | undefined
  files: ConversationFile[]
  isDraft: boolean
  isMedicalValue: boolean
  messageFieldValue: string
}

export const getMessageAction = ({
  editMessage,
  isDraft,
  conversationId,
  messageFieldValue,
  files,
  isMedicalValue,
}: DispatchMessageParams) => {
  const messageStatus = isDraft ? MessageStatus.DRAFT : MessageStatus.SENT

  if (editMessage) {
    return updateMessage({
      messageId: editMessage.id,
      status: messageStatus,
      text: messageFieldValue,
      files,
      isMedical: isMedicalValue,
    })
  }

  if (isDraft) {
    return sendConversationDraftMessage({
      conversationId,
      messageType: MessageType.PLAIN,
      body: messageFieldValue,
      isMedical: isMedicalValue,
    })
  }

  return sendConversationMessage({
    conversationId,
    messageType: MessageType.PLAIN,
    body: messageFieldValue,
    files,
    isMedical: isMedicalValue,
  })
}

export const validateMessageSend = (
  isDraft: boolean,
  isMedicalValue: boolean,
  setMedicalValidationError: (error: string | undefined) => void,
  validate: () => boolean,
  t: (key: string) => string,
): boolean => {
  if (isDraft && isMedicalValue) {
    setMedicalValidationError(
      t('Communications:MEDICAL_RECORDS.CANNOT_SAVE_AS_DRAFT'),
    )
    return false
  }

  setMedicalValidationError(undefined)
  return validate()
}

export const handleArchiveToggle = (
  status: string | null | undefined,
  conversationId: string,
  dispatch: Dispatch,
) => {
  if (status !== ConversationStatus.ARCHIVED) {
    dispatch(
      queueEasterEggEvent({
        actionType: EasterEggEvents.ARCHIVE_CONVERSATION,
      }),
    )
  }
  dispatch(toggleArchiveForConversation(conversationId))
}

export const syncPreferredTransport = (
  editMessage: ConversationMessage | undefined,
  transportFieldValue: string,
  preferredTransport: ConversationTransport | undefined,
  setValue: (value: ConversationTransport) => void,
) => {
  if (!editMessage && R.isEmpty(transportFieldValue) && preferredTransport) {
    setValue(preferredTransport)
  }
}

export const openNewThreadReply = (
  latestMessage: ConversationMessage | undefined,
  conversation: Conversation | undefined,
  openDialog: (params: {
    conversation: Conversation
    messageId: string
  }) => void,
) => {
  if (!latestMessage || !conversation) return
  openDialog({
    conversation,
    messageId: latestMessage.id,
  })
}
